// src/pages/DeviceManagement/components/FilterPanel/SavedFilters.jsx
// Manage saved filter presets
// Part of Device Management Overhaul - Phase 3

import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  IconButton,
  Divider,
  Tooltip,
  Alert
} from '@mui/material';
import {
  Save as SaveIcon,
  Bookmark as BookmarkIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Add as AddIcon,
  ArrowDropDown as ArrowDownIcon
} from '@mui/icons-material';

// Local storage service for saved filters
const STORAGE_KEY = 'deviceManagement.savedFilters';
const MAX_SAVED_FILTERS = 10;

const SavedFilters = ({ filters, onFilterChange, currentFilterCount }) => {
  const [savedFilters, setSavedFilters] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [filterName, setFilterName] = useState('');
  const [editingFilter, setEditingFilter] = useState(null);
  const [error, setError] = useState('');

  // Load saved filters from localStorage
  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        // Convert quickFilters back to Set
        const filters = parsed.map(f => ({
          ...f,
          filters: {
            ...f.filters,
            quickFilters: new Set(f.filters.quickFilters || [])
          }
        }));
        setSavedFilters(filters);
      }
    } catch (error) {
      console.error('Error loading saved filters:', error);
    }
  }, []);

  // Save filters to localStorage
  const persistFilters = (filters) => {
    try {
      // Convert Sets to Arrays for JSON serialization
      const serializable = filters.map(f => ({
        ...f,
        filters: {
          ...f.filters,
          quickFilters: Array.from(f.filters.quickFilters || [])
        }
      }));
      localStorage.setItem(STORAGE_KEY, JSON.stringify(serializable));
    } catch (error) {
      console.error('Error saving filters:', error);
      setError('Failed to save filters');
    }
  };

  const handleOpenMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const handleSaveFilter = () => {
    if (!filterName.trim()) {
      setError('Please enter a filter name');
      return;
    }

    if (!currentFilterCount) {
      setError('No active filters to save');
      return;
    }

    const newFilter = {
      id: editingFilter?.id || Date.now().toString(),
      name: filterName.trim(),
      filters: {
        ...filters,
        quickFilters: Array.from(filters.quickFilters || [])
      },
      createdAt: editingFilter?.createdAt || new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    let updatedFilters;
    if (editingFilter) {
      // Update existing filter
      updatedFilters = savedFilters.map(f =>
        f.id === editingFilter.id ? newFilter : f
      );
    } else {
      // Add new filter
      if (savedFilters.length >= MAX_SAVED_FILTERS) {
        setError(`Maximum ${MAX_SAVED_FILTERS} saved filters allowed`);
        return;
      }
      updatedFilters = [newFilter, ...savedFilters];
    }

    setSavedFilters(updatedFilters);
    persistFilters(updatedFilters);
    setSaveDialogOpen(false);
    setFilterName('');
    setEditingFilter(null);
    setError('');
  };

  const handleLoadFilter = (savedFilter) => {
    onFilterChange({
      ...savedFilter.filters,
      quickFilters: new Set(savedFilter.filters.quickFilters || [])
    });
    handleCloseMenu();
  };

  const handleDeleteFilter = (filterId, event) => {
    event.stopPropagation();
    const updatedFilters = savedFilters.filter(f => f.id !== filterId);
    setSavedFilters(updatedFilters);
    persistFilters(updatedFilters);
  };

  const handleEditFilter = (filter, event) => {
    event.stopPropagation();
    setEditingFilter(filter);
    setFilterName(filter.name);
    setSaveDialogOpen(true);
  };

  const openSaveDialog = () => {
    setEditingFilter(null);
    setFilterName('');
    setError('');
    setSaveDialogOpen(true);
  };

  return (
    <>
      <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
        {/* Save Current Filters Button */}
        <Tooltip title="Save current filter configuration">
          <Button
            variant="outlined"
            size="small"
            startIcon={<SaveIcon />}
            onClick={openSaveDialog}
            disabled={!currentFilterCount}
            sx={{ textTransform: 'none' }}
          >
            Save Filters
          </Button>
        </Tooltip>

        {/* Saved Filters Dropdown */}
        {savedFilters.length > 0 && (
          <Button
            variant="contained"
            size="small"
            startIcon={<BookmarkIcon />}
            endIcon={<ArrowDownIcon />}
            onClick={handleOpenMenu}
            sx={{ textTransform: 'none' }}
          >
            Saved Filters ({savedFilters.length})
          </Button>
        )}
      </Box>

      {/* Saved Filters Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
        PaperProps={{
          sx: {
            maxHeight: 400,
            width: 300
          }
        }}
      >
        <MenuItem disabled>
          <Typography variant="caption" color="textSecondary">
            Select a saved filter to apply
          </Typography>
        </MenuItem>
        <Divider />
        {savedFilters.map((savedFilter) => (
          <MenuItem
            key={savedFilter.id}
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              '&:hover .actions': {
                visibility: 'visible'
              }
            }}
          >
            <ListItemText
              primary={savedFilter.name}
              secondary={`Created ${new Date(savedFilter.createdAt).toLocaleDateString()}`}
              onClick={() => handleLoadFilter(savedFilter)}
              primaryTypographyProps={{ variant: 'body2' }}
              secondaryTypographyProps={{ variant: 'caption' }}
            />
            <Box
              className="actions"
              sx={{
                display: 'flex',
                visibility: 'hidden',
                gap: 0.5
              }}
            >
              <IconButton
                size="small"
                onClick={(e) => handleEditFilter(savedFilter, e)}
              >
                <EditIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                onClick={(e) => handleDeleteFilter(savedFilter.id, e)}
                color="error"
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          </MenuItem>
        ))}
      </Menu>

      {/* Save Filter Dialog */}
      <Dialog
        open={saveDialogOpen}
        onClose={() => setSaveDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editingFilter ? 'Update Saved Filter' : 'Save Current Filters'}
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}
          <TextField
            autoFocus
            margin="dense"
            label="Filter Name"
            fullWidth
            variant="outlined"
            value={filterName}
            onChange={(e) => setFilterName(e.target.value)}
            placeholder="e.g., Offline Tablets in Building A"
            helperText={`Give this filter set a descriptive name (${savedFilters.length}/${MAX_SAVED_FILTERS} saved)`}
          />
          <Typography variant="caption" color="textSecondary" sx={{ mt: 2, display: 'block' }}>
            Current filters: {currentFilterCount} active filter{currentFilterCount !== 1 ? 's' : ''}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSaveDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleSaveFilter}
            variant="contained"
            startIcon={editingFilter ? <EditIcon /> : <SaveIcon />}
          >
            {editingFilter ? 'Update' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SavedFilters;